import { usePushNotifications } from '@common/push-notifications-layer/composables/usePushNotifications'

/**
 * Firebase Cloud Messaging plugin (client-only)
 *
 * Runs after authentication is established (see plugin order in nuxt.base.config.ts).
 * Responsibilities:
 *  1. Register the SW → app message bridge immediately (before any notification click)
 *  2. Request push notification permission and register the FCM token with the backend
 *  3. Set up a foreground message handler so notifications show while the app is open
 */
export default defineNuxtPlugin(() => {
  const pushNotifications = usePushNotifications()
  if (!pushNotifications.isFirebaseMessagingConfigured()) {
    return
  }

  const { requestAndRegisterPushToken, setupForegroundMessageHandler, setupSWMessageListener, disposePushNotificationSideEffects } =
    pushNotifications

  // Must be wired up first — a notification click could arrive before the async
  // permission/token flow completes
  const stopSwBridge = setupSWMessageListener()

  const teardownFcmPluginSideEffects = () => {
    stopSwBridge()
    disposePushNotificationSideEffects()
  }

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      teardownFcmPluginSideEffects()
    })
  }

  // Permission request + token registration and foreground handler run in the
  // background so the plugin does not block rendering; teardown stays synchronous above.
  void (async () => {
    try {
      await requestAndRegisterPushToken()
    } catch (err) {
      console.error('[firebase-messaging] Push token setup failed:', err)
    }

    try {
      await setupForegroundMessageHandler()
    } catch (err) {
      console.error('[firebase-messaging] Foreground handler failed:', err)
    }
  })()
})
